<script setup>
// Um botao so no canto para todos os assistentes da conta. Com um assistente,
// o botao abre ele direto; com mais de um, abre o menu para escolher.
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { onClickOutside } from '@vueuse/core';
import { useMapGetter } from 'dashboard/composables/store';
import { useDraggableFloat } from 'dashboard/composables/useDraggableFloat';
import { useCopilotLauncher } from 'dashboard/composables/useCopilotLauncher';
import {
  hubAnchor,
  isSupportPanelOpen,
  JARVIS_TOGGLE_EVENT,
} from 'dashboard/composables/useAssistantHub';
import { emitter } from 'shared/helpers/mitt';

const props = defineProps({
  jarvisAvailable: { type: Boolean, default: false },
});

const { t } = useI18n();
const accountId = useMapGetter('getCurrentAccountId');
const isModuleEnabledonAccount = useMapGetter(
  'accounts/isModuleEnabledonAccount'
);
const { showCopilotLauncher, toggleCopilotPanel } = useCopilotLauncher();

// Mesmo lugar em que a bolha do Jarvis ja ficava: acima da caixa de resposta,
// sem cobrir o Enviar da conversa nem o Proximo do Disparador.
const { position, style, onPointerDown, consumeDragClick } =
  useDraggableFloat('assistant_hub_position', () =>
    window.innerWidth < 768
      ? { right: 16, bottom: 150 }
      : { right: 24, bottom: 150 }
  );
watch(position, value => (hubAnchor.value = value), { immediate: true });

const isMenuOpen = ref(false);
const hubRef = ref(null);
onClickOutside(hubRef, () => (isMenuOpen.value = false));

const items = computed(() =>
  [
    {
      key: 'support',
      icon: 'i-lucide-life-buoy',
      label: t('ASSISTANT_HUB.SUPPORT'),
      show: isModuleEnabledonAccount.value(
        accountId.value,
        'support_assistant'
      ),
      open: () => (isSupportPanelOpen.value = !isSupportPanelOpen.value),
    },
    {
      key: 'jarvis',
      icon: 'i-lucide-bot',
      label: t('ASSISTANT_HUB.JARVIS'),
      show: props.jarvisAvailable,
      open: () => emitter.emit(JARVIS_TOGGLE_EVENT),
    },
    {
      key: 'captain',
      icon: 'i-woot-captain',
      label: t('ASSISTANT_HUB.CAPTAIN'),
      show: showCopilotLauncher.value,
      open: toggleCopilotPanel,
    },
  ].filter(item => item.show)
);

const buttonIcon = computed(() =>
  items.value.length === 1 ? items.value[0].icon : 'i-lucide-sparkles'
);
const buttonLabel = computed(() =>
  items.value.length === 1 ? items.value[0].label : t('ASSISTANT_HUB.OPEN')
);

const choose = item => {
  isMenuOpen.value = false;
  item.open();
};

const onButtonClick = () => {
  if (consumeDragClick()) return;
  if (items.value.length === 1) {
    choose(items.value[0]);
    return;
  }
  isMenuOpen.value = !isMenuOpen.value;
};
</script>

<template>
  <div
    v-if="items.length"
    ref="hubRef"
    class="fixed z-40 flex flex-col items-end gap-2"
    :style="style"
  >
    <ul
      v-if="isMenuOpen"
      role="menu"
      class="flex flex-col p-1 mb-0 list-none border shadow-xl min-w-44 rounded-xl bg-n-solid-2 border-n-weak"
    >
      <li v-for="item in items" :key="item.key" role="none">
        <button
          type="button"
          role="menuitem"
          class="flex items-center w-full gap-2 px-3 py-2 text-sm rounded-lg text-n-slate-12 hover:bg-n-alpha-2"
          @click="choose(item)"
        >
          <span class="size-4 shrink-0" :class="item.icon" />
          {{ item.label }}
        </button>
      </li>
    </ul>
    <button
      type="button"
      class="grid text-white rounded-full shadow-xl size-14 place-items-center bg-n-brand hover:brightness-110 touch-none cursor-grab active:cursor-grabbing"
      :title="buttonLabel"
      :aria-label="buttonLabel"
      :aria-expanded="isMenuOpen"
      @pointerdown="onPointerDown"
      @click="onButtonClick"
    >
      <span class="size-6" :class="buttonIcon" />
    </button>
  </div>
</template>
